import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

export default function Register() {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');

  const [formData, setFormData] = useState({
    companyName: '',
    companySlug: '',
    email: '',
    name: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      await loginWithGoogle(credentialResponse.credential, referralCode);
      toast.success('Conta com Google criada e logada com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta com o Google');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value,
      // Auto-generate slug when typing company name
      ...(id === 'companyName' ? {
        companySlug: value.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
      } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register({
        company_data: {
          name: formData.companyName,
          slug: formData.companySlug,
          email: formData.email
        },
        user_data: {
          email: formData.email,
          password: formData.password,
          name: formData.name
        }
      }, referralCode);
      toast.success('Conta criada com sucesso! Seja bem-vindo ao Kode Studio.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta. Verifique os dados fornecidos.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-8">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_ee8574e7-2cba-4c3d-8cd6-b25471143ee6/artifacts/31800g12_Logo%20Login%20Sites.png" 
              alt="Kode Studio" 
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-heading font-bold text-slate-900">Crie sua Conta</h1>
            <p className="text-slate-500 mt-2">Inicie com o Plano Básico totalmente livre</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label htmlFor="companyName">Nome da sua Empresa</Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Nome do seu negócio"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="name">Seu Nome Completo</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Seu nome"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email">Email Corporativo</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="seu@email.com"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Senha Forte</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="mt-1"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90 mt-6"
              disabled={loading}
            >
              {loading ? 'Criando Conta...' : 'Cadastrar e Acessar'}
            </Button>
          </form>

          <div className="relative my-6 text-center">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
            <div className="relative flex justify-center text-sm"><span className="bg-white px-2 text-slate-500">Cadastro expresso</span></div>
          </div>

          <div className="flex justify-center w-full mb-6 relative">
            <div className="w-full h-11 flex justify-center [&>div]:w-full [&>div>div]:w-full">
              <GoogleLogin 
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Falha ao autenticar com o Google.')}
                shape="rectangular"
                theme="outline"
                size="large"
                text="signup_with"
                locale="pt-BR"
              />
            </div>
          </div>

          <div className="mt-6 text-center text-sm text-slate-500">
            <p>Já possui uma conta? <Link to="/login" className="text-primary hover:underline font-semibold">Fazer Login</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
